import { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { Mail, ArrowRight, ArrowLeft, Shield, Zap, Star, MessageSquare, RefreshCw } from 'lucide-react';
import { Turnstile } from '@repo/ui/turnstile';
import { HoneypotField } from '@repo/ui/honeypot-field';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Checkbox } from '../components/ui/checkbox';
import { useAuth } from '../context/AuthContext';
import { authService } from '../services/api';
import ClickProLogo from '../components/ClickProLogo';

const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY;

export default function Auth() {
  const navigate = useNavigate();
  const location = useLocation();
  const { refreshUser } = useAuth();
  const [step, setStep] = useState('email'); // 'email' | 'code'
  const [email, setEmail] = useState('');
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [debugCode, setDebugCode] = useState('');
  const [rememberMe, setRememberMe] = useState(true);
  const [website, setWebsite] = useState(''); // honeypot — real users never fill this
  const [sendCaptchaToken, setSendCaptchaToken] = useState('');
  const [resendCaptchaToken, setResendCaptchaToken] = useState('');
  const [resendWidgetKey, setResendWidgetKey] = useState(0);

  const codeInputRefs = useRef([]);
  
  // Timer pour le cooldown de renvoi
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);
  
  // Focus sur le premier input de code quand on arrive à l'étape code
  useEffect(() => {
    if (step === 'code' && codeInputRefs.current[0]) {
      codeInputRefs.current[0].focus();
    }
  }, [step]);

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    
    if (!email.trim() || !email.includes('@')) {
      toast.error('Veuillez entrer une adresse email valide');
      return;
    }
    if (TURNSTILE_SITE_KEY && !sendCaptchaToken) {
      toast.error('Merci de valider la vérification anti-robot');
      return;
    }

    setLoading(true);
    try {
      const response = await authService.sendCode(email.trim(), { captchaToken: sendCaptchaToken, website });
      toast.success('Un code de connexion vous a été envoyé par email');
      
      if (response.debug_code) {
        setDebugCode(response.debug_code);
      }
      
      setStep('code');
      setResendCooldown(60);
    } catch (error) {
      console.error('Send code error:', error);
      toast.error(error.message || 'Erreur lors de l\'envoi du code');
    } finally {
      setLoading(false);
    }
  };

  const handleCodeChange = (index, value) => {
    // Ne garder que les chiffres
    const digit = value.replace(/\D/g, '').slice(-1);
    
    const newCode = [...code];
    newCode[index] = digit;
    setCode(newCode);
    
    // Auto-focus sur le champ suivant
    if (digit && index < 5) {
      codeInputRefs.current[index + 1]?.focus();
    }
    
    // Si tous les champs sont remplis, soumettre automatiquement
    const fullCode = newCode.join('');
    if (fullCode.length === 6) {
      handleCodeSubmit(fullCode);
    }
  };

  const handleKeyDown = (index, e) => {
    // Retour arrière
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      codeInputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pastedData.length === 6) {
      const newCode = pastedData.split('');
      setCode(newCode);
      handleCodeSubmit(pastedData);
    }
  };

  const handleCodeSubmit = async (fullCode = code.join('')) => {
    if (fullCode.length !== 6) {
      toast.error('Veuillez entrer le code à 6 chiffres');
      return;
    }

    setLoading(true);
    try {
      const response = await authService.verifyCode(email.trim(), fullCode, rememberMe);
      
      await refreshUser();
      
      toast.success(response.message);
      
      const redirectTo = location.state?.from || '/tableau-de-bord';
      
      if (!response.profile_completed) {
        navigate('/tableau-de-bord/mon-profil', { state: { firstLogin: true } });
      } else {
        navigate(redirectTo);
      }
    } catch (error) {
      console.error('Verify code error:', error);
      toast.error(error.message || 'Code incorrect');
      // Réinitialiser le code
      setCode(['', '', '', '', '', '']);
      codeInputRefs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (resendCooldown > 0) return;
    if (TURNSTILE_SITE_KEY && !resendCaptchaToken) {
      toast.error('Merci de valider la vérification anti-robot');
      return;
    }

    setLoading(true);
    try {
      const response = await authService.resendCode(email.trim(), { captchaToken: resendCaptchaToken, website });
      toast.success('Un nouveau code vous a été envoyé');
      
      if (response.debug_code) {
        setDebugCode(response.debug_code);
      }
      
      setResendCooldown(60);
      setCode(['', '', '', '', '', '']);
      codeInputRefs.current[0]?.focus();
    } catch (error) {
      toast.error(error.message || 'Erreur lors du renvoi');
    } finally {
      setLoading(false);
      // Turnstile tokens are single-use — remount the widget for a fresh one.
      setResendCaptchaToken('');
      setResendWidgetKey((k) => k + 1);
    }
  };

  const handleBack = () => {
    setStep('email');
    setCode(['', '', '', '', '', '']);
    setDebugCode('');
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Panel - Form */}
      <div className="w-full lg:w-1/2 flex flex-col justify-center px-6 sm:px-12 lg:px-16 py-12">
        <div className="w-full max-w-md mx-auto">
          {/* Logo */}
          <a href="https://clicketpro.fr" className="inline-block mb-8">
            <ClickProLogo />
          </a>

          {/* Step Email */}
          {step === 'email' && (
            <div className="animate-fade-in">
              <h1 className="text-3xl font-bold text-slate-900 mb-2">Connexion</h1>
              <p className="text-slate-500 mb-8">
                Entrez votre email pour recevoir un code de connexion
              </p>

              <form onSubmit={handleEmailSubmit} className="space-y-5">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Adresse e-mail
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <Input
                      type="email"
                      placeholder="exemple@email.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="input-clickpro pl-12"
                      autoFocus
                      data-testid="input-email"
                    />
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <Checkbox
                    id="remember-me"
                    checked={rememberMe}
                    onCheckedChange={(checked) => setRememberMe(checked === true)}
                    className="data-[state=checked]:bg-[hsl(var(--primary))] data-[state=checked]:border-[hsl(var(--primary))]"
                  />
                  <label htmlFor="remember-me" className="text-sm text-slate-600 cursor-pointer">
                    Rester connecté
                  </label>
                </div>

                <HoneypotField value={website} onChange={setWebsite} />

                {TURNSTILE_SITE_KEY && (
                  <Turnstile
                    siteKey={TURNSTILE_SITE_KEY}
                    onVerify={setSendCaptchaToken}
                    onExpire={() => setSendCaptchaToken('')}
                  />
                )}

                <Button
                  type="submit"
                  disabled={loading || (Boolean(TURNSTILE_SITE_KEY) && !sendCaptchaToken)}
                  className="w-full btn-clickpro-primary h-12 text-base"
                  data-testid="btn-send-code"
                >
                  {loading ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
                  ) : (
                    <>
                      Recevoir un code
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </>
                  )}
                </Button>
              </form>

              <p className="text-center mt-8 text-sm text-slate-500">
                En continuant, vous acceptez nos{' '}
                <a href="#" className="text-[hsl(var(--primary))] hover:underline">conditions d'utilisation</a>
                {' '}et notre{' '}
                <a href="#" className="text-[hsl(var(--primary))] hover:underline">politique de confidentialité</a>
              </p>
            </div>
          )}

          {/* Step Code */}
          {step === 'code' && (
            <div className="animate-fade-in">
              <button 
                onClick={handleBack}
                className="flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-6 transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                Modifier l'email
              </button>
              
              <h1 className="text-3xl font-bold text-slate-900 mb-2">Vérification</h1>
              <p className="text-slate-500 mb-2">
                Entrez le code envoyé à
              </p>
              <p className="text-slate-900 font-medium mb-8">{email}</p>

              {/* Debug code (dev only) */}
              {debugCode && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-6">
                  <p className="text-amber-800 text-sm">
                    <strong>Mode développement</strong> - Code : <span className="font-mono font-bold">{debugCode}</span>
                  </p>
                </div>
              )}

              {/* Code inputs */}
              <div className="flex justify-center gap-3 mb-6" onPaste={handlePaste}>
                {code.map((digit, index) => (
                  <Input
                    key={index}
                    ref={(el) => (codeInputRefs.current[index] = el)}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleCodeChange(index, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(index, e)}
                    className="w-12 h-14 text-center text-2xl font-bold input-clickpro"
                    data-testid={`code-input-${index}`}
                  />
                ))}
              </div>

              <Button
                onClick={() => handleCodeSubmit()}
                disabled={loading || code.join('').length !== 6}
                className="w-full btn-clickpro-primary h-12 text-base mb-4"
                data-testid="btn-verify-code"
              >
                {loading ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
                ) : (
                  <>
                    Valider
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </>
                )}
              </Button>

              {TURNSTILE_SITE_KEY && (
                <div className="flex justify-center mb-4">
                  <Turnstile
                    key={resendWidgetKey}
                    siteKey={TURNSTILE_SITE_KEY}
                    onVerify={setResendCaptchaToken}
                    onExpire={() => setResendCaptchaToken('')}
                  />
                </div>
              )}

              <div className="text-center">
                <button
                  onClick={handleResendCode}
                  disabled={resendCooldown > 0 || loading || (Boolean(TURNSTILE_SITE_KEY) && !resendCaptchaToken)}
                  className={`inline-flex items-center gap-2 text-sm ${
                    resendCooldown > 0
                      ? 'text-slate-400 cursor-not-allowed'
                      : 'text-[hsl(var(--primary))] hover:underline cursor-pointer'
                  }`}
                  data-testid="btn-resend"
                >
                  <RefreshCw className="w-4 h-4" />
                  {resendCooldown > 0
                    ? `Renvoyer le code (${resendCooldown}s)`
                    : 'Renvoyer le code'
                  }
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Right Panel - Editorial Image + Trust */}
      <div className="hidden lg:flex lg:w-1/2 relative">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center grain-overlay"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1503387762-592deb58ef4e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwxfHxhcmNoaXRlY3QlMjBjb25zdHJ1Y3Rpb24lMjBwbGFucyUyMGhhbmRzJTIwdGFibGV8ZW58MHx8fHwxNzgwOTIwOTYyfDA&ixlib=rb-4.1.0&q=85)'
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-br from-[#0D1B2A]/85 via-[#0D1B2A]/55 to-[#0D1B2A]/35"></div>
        </div>

        {/* Content over image */}
        <div className="relative z-10 flex flex-col justify-between p-14 text-white w-full">
          <div>
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-md border border-white/20 px-4 py-1.5 rounded-full text-[11px] uppercase tracking-[0.22em] mb-10">
              <Shield className="w-3.5 h-3.5" />
              Connexion sécurisée
            </div>
            <h2 className="font-display text-4xl xl:text-5xl font-bold leading-[1.08] tracking-tight max-w-md">
              L&apos;artisanat,<br/>
              <span className="text-accent">retrouvé.</span>
            </h2>
            <p className="text-white/75 mt-8 max-w-md text-[15px] leading-relaxed">
              Une plateforme pensée pour rapprocher les particuliers des meilleurs professionnels français — sans mot de passe, sans friction.
            </p>
          </div>

          <div className="border-t border-white/15 pt-8 grid grid-cols-4 gap-6">
            <div>
              <Shield className="w-5 h-5 mb-2 text-[hsl(var(--accent))]" />
              <p className="text-sm font-semibold">Pros vérifiés</p>
              <p className="text-xs text-white/55 mt-0.5">Contrôlés &amp; notés</p>
            </div>
            <div>
              <Zap className="w-5 h-5 mb-2 text-[hsl(var(--accent))]" />
              <p className="text-sm font-semibold">Réponse rapide</p>
              <p className="text-xs text-white/55 mt-0.5">Quelques minutes</p>
            </div>
            <div>
              <Star className="w-5 h-5 mb-2 text-[hsl(var(--accent))]" />
              <p className="text-sm font-semibold">Avis clients</p>
              <p className="text-xs text-white/55 mt-0.5">Milliers d&apos;avis</p>
            </div>
            <div>
              <MessageSquare className="w-5 h-5 mb-2 text-[hsl(var(--accent))]" />
              <p className="text-sm font-semibold">Messagerie</p>
              <p className="text-xs text-white/55 mt-0.5">En confiance</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
